import { useMemo, useState } from "react";
import type { ColumnFiltersState, PaginationState } from "@tanstack/react-table";
import { Plus } from "lucide-react";

import { pageCount } from "@/core/api/pagination";
import { FLASHPAY_ROLES, RequireRole } from "@/core/auth";
import { DataTableClar } from "@/core/components/shared/data-table";
import { Button } from "@/core/components/ui/button";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import type { NotificationProviderFilter } from "@/features/notification/api/dto";
import { useNotificationProviders } from "@/features/notification/api/use-notification-providers";
import {
  NotificationProviderFormDialog,
  notificationProvidersColumns,
  type NotificationProviderRow,
} from "@/features/notification/components";

const toProviderFilter = (
  filters: ColumnFiltersState
): NotificationProviderFilter => {
  const filter: NotificationProviderFilter = {};
  for (const { id, value } of filters) {
    if (typeof value !== "string" || !value) continue;
    if (id === "code") filter.code = value;
    if (id === "channel")
      filter.channel = value as NotificationProviderFilter["channel"];
    if (id === "countryCode") filter.countryCode = value;
  }
  return filter;
};

/**
 * Registre de routage des fournisseurs de transport
 * (`POST /NotificationProviders/all`, guide v2.3 §6.7).
 * Écriture réservée aux rôles `flashpay-superadmin` / `flashpay-admin`.
 */
export function NotificationProvidersPage() {
  const { t } = useTypedTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const params = useMemo(
    () => ({
      filter: toProviderFilter(columnFilters),
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    }),
    [columnFilters, pagination]
  );

  const { data, isLoading, isFetching, error, refetch } =
    useNotificationProviders(params);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex justify-between items-start gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold text-foreground">
            {t("notifications.providers.title")}
          </h1>
          <p className="text-sm text-muted-foreground max-w-3xl">
            {t("notifications.providers.subtitle")}
          </p>
        </div>
        <RequireRole roles={[FLASHPAY_ROLES.SUPERADMIN, FLASHPAY_ROLES.ADMIN]}>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 w-4 h-4" />
            {t("notifications.providers.create")}
          </Button>
        </RequireRole>
      </div>

      <DataTableClar
        data={(data?.items ?? []).filter(
          (p): p is NotificationProviderRow => Boolean(p.id)
        )}
        columnsClar={notificationProvidersColumns}
        title={t("notifications.providers.title")}
        exportFileName="notification-providers"
        paginationMode="server"
        serverPageCount={data ? pageCount(data) : 0}
        totalItemsCount={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        loading={{ isLoading, isFetching }}
        error={
          error
            ? { message: error.message, refetch: () => void refetch() }
            : undefined
        }
      />

      <NotificationProviderFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
      />
    </section>
  );
}
